import React, { useMemo,useState,useEffect } from 'react';
import { sdk } from '@/sdk/CommercetoolsSDK';
import Typography from '@/components/atoms/typography';
import { classnames } from '@/utils/classnames/classnames';
import Breadcrumb from '@/components/molecules/breadcrumb';
import Link from '@/components/atoms/link';
import { PDPHeaderProps } from '../types';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { HomeIcon, ChevronRightIcon,StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import DeliveryDetails from './delivery-details';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { isExclusiveProductEnabled } from '@/utils/feature-flag';
import useFormat from '@/hooks/useFormat';
import { useParams } from 'next/navigation';
import { FaInfoCircle } from "react-icons/fa";

import { exclusivityMapping } from '@/constants/exclusivityMapping';
import { getLifecycleBadge, getIntroductoryPriceBadge } from '@/utils/product-lifecycle';
import { buildPdpBreadcrumbs } from '@/utils/category-breadcrumb/build-pdp-breadcrumbs';

const Header = ({
  className,
  product,
  variant,
  itemCode,
  productInventory,
  itemName,
  customerQuantityPrice,
  deliveryEstimate,
  isLoading,
  isPriceLoading,
  variationUI,
  productType,
  isBuilderRange,
  setQuantity,
  quantityPrice,
  quantity,
  variantKey,
  quantityPriceLoading,
  setPostalCode,
  postalCode,
  loadingDeliverEstimate,
  warehouse,
  warehouseqty,
  handleAddToProject,
  hasOutOfStockProductPart
}: PDPHeaderProps) => {
  const { formatCurrency } = useFormat()
  const { locale } = useParams();
  const localeString = Array.isArray(locale) ? locale[0] : locale || '';
  const headerClassName = classnames('pb-0 md:pb-4 pt-0 md:pt-4 lg:pt-6 pdp-header', className);
  const lifecycleBadge = useMemo(() => getLifecycleBadge(product?.lifecycle), [product?.lifecycle]);
  const introPriceBadge = useMemo(
    () => getIntroductoryPriceBadge(itemCode) || getIntroductoryPriceBadge(product?.sku),
    [itemCode, product?.sku],
  );
    
  //   const exclusiveProductNZ = 
  //     extractAttr(product?.attributesAlgolia?.['exclusive-product-NZ']) || 
  //     extractAttr(product?.attributes?.['exclusive-product-NZ']) ||
  //     extractAttr(variant?.attributes?.['exclusive-product-NZ']) ||
  //     extractAttr(product?.variants?.find((v: any) => v.key === variantKey)?.attributes?.['exclusive-product-NZ']) ||
  //     extractAttr(product?.variants?.[0]?.attributes?.['exclusive-product-NZ']) ||
  //     extractAttr(valObj);
      
  //   return exclusiveProductNZ === 'PLUMWO';
  // }, [product, variant, variantKey]);

  const breadcrumbs = useMemo(
    () => buildPdpBreadcrumbs(product?.categories ?? [], localeString),
    [product?.categories, localeString],
  );
  // console.log('isBuilderRange',isBuilderRange)
const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    const checkWishlist = async () => {
      try {
        const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';
        const response = await sdk.composableCommerce.custom.getCustomerWishlist({
          pageNumber: 1,
          pageSize: 100,
          storeKey
        });
        if (!response.isError && response.data?.data?.items) {
          const wishlisted = response.data.data.items.some(
            (item: any) => String(item.variantsku).trim() === String(product?.sku ?? '').trim()
          );
          setIsWishlisted(wishlisted);
        }
      } catch {
        // silent — star stays unfilled
      }
    };
    if (product?.sku) {
      checkWishlist();
    }
  }, [product?.sku]);

  const handleAddToWishlist = async () => {
    try {
      const storeKey = locale === 'au' ? 'plumbershub-au' : 'plumbershub-nz';
      if (isWishlisted) {
        const response = await sdk.composableCommerce.custom.removeProductFromWishlist({ variantSku: product?.sku, storeKey });
        if (!response.isError) {
          setIsWishlisted(false);
        }
        return;
      }
      const response = await sdk.composableCommerce.custom.addProductToWishlist(
        JSON.stringify({ variantSku: product?.sku, storeKey }),
      );
      if (!response.isError) {
        setIsWishlisted(true);
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    }
  };
  return (
    <div className={headerClassName}>
      {breadcrumbs.length > 0 && (
        <Breadcrumb className="mt-3 hidden pb-3 md:flex" Separator={<ChevronRightIcon className="w-4" />}>
          <Link link="/" className="capitalize">
            {/* {translate('common.home')} */}
            <HomeIcon className="w-[16px]  !text-[#173a5f] font-normal hover:text-gray-700" />
          </Link>
          {breadcrumbs.map((crumb: any, index: number) => (
            <Link
              key={index}
              link={crumb.link}
              className="text-12 capitalize hover:!no-underline hover:!underline-offset-0 hover:!text-[#000000] hover:!text-[12px] leading-4 text-[#000000]"
            >
              {crumb.name}
            </Link>
          ))}
          <Typography className="cursor-default !text-[12px] !leading-4 text-[#525252]">{product.name}</Typography>
        </Breadcrumb>
      )}
      <div className="flex flex-col lg:flex-row">
        <div className="mb-2 flex w-full flex-col md:mb-0 md:mr-4 lg:w-[70%]">
          <div className='flex flex-wrap gap-4' >
            {Array.isArray(product.exclusiveProduct) &&
              product.exclusiveProduct
                .filter((p: any) => p && String(p).trim().toLowerCase() !== 'null')
                .map((p: any, index: number) => {
                  const partnerName = exclusivityMapping[p?.toUpperCase()] || String(p).toUpperCase();
                  return (
                    <span
                      key={index}
                      className="!bg-[#A6C9CC] !text-[#000000] !text-center px-1 py-0.5 lg:py-0.5 mt-[5px] mb-[5px] !rounded-[10px] w-auto h-[22px] lg:h-[24px] flex justify-center items-center text-[10px] lg:text-xs"
                    >
                      EXCLUSIVE TO {partnerName}
                    </span>
                  );
                })}

            {/* {product?.isNew && !lifecycleBadge && (
              <span className='!bg-[#FA2727] !text-[#FFFFFF] !text-center py-0.5 lg:py-0.5 mt-[5px] mb-[5px] !rounded-[10px] px-4 h-[22px] lg:h-[24px] flex justify-center items-center text-[10px] lg:text-xs font-semibold' >
                NEW
              </span>
            )} */}
            {lifecycleBadge && (
              <span
                className='!text-[#FFFFFF] !text-center py-0.5 lg:py-0.5 mt-[5px] mb-[5px] !rounded-[10px] px-4 h-[22px] lg:h-[24px] flex justify-center items-center text-[10px] lg:text-xs font-semibold'
                style={{ backgroundColor: lifecycleBadge.color }}
              >
                {lifecycleBadge.label.toUpperCase()}
              </span>
            )}
            {introPriceBadge && (
              <span
                className='!text-[#FFFFFF] !text-center py-0.5 lg:py-0.5 mt-[5px] mb-[5px] !rounded-[10px] px-4 h-[22px] lg:h-[24px] flex justify-center items-center text-[10px] lg:text-xs font-semibold'
                style={{ backgroundColor: introPriceBadge.color }}
              >
                {introPriceBadge.label.toUpperCase()}
              </span>
            )}
            {
              locale === 'au' && product?.isGSTExempt && (
                <span className='!bg-black !text-white !text-center py-[2px] mt-[5px] mb-[5px] !rounded-[10px] w-[120px] lg:w-[130px] h-[22px] lg:h-[24px] flex justify-center items-center text-[10px] lg:text-xs'>
                  GST FREE
                </span>
              )
            }
          </div>
          <div className="mt-2 md:mt-0 flex gap-6 align-baseline">
            <Typography
              className="text-[22px] lg:text-[30px] text-[#090909] font-semibold leading-snug lg:leading-snug 2xl:!text-[34px]"
            >
              {itemName ?? product.name}
            </Typography>

          </div>
          {itemCode &&
            <div className="mt-1 lg:mt-2 flex gap-2">
              <Typography className=" text-black " fontSize={18} lineHeight="loose">
                {"Product Code:"}
              </Typography>
              <Typography className=" text-black" fontSize={18} lineHeight="loose">
                {itemCode}
              </Typography>
            </div>
          }

          {!isBuilderRange && !product?.lifecycle?.toUpperCase()?.trim().includes('PROTOTYPE') ? <div className="flex gap-1 mt-2 lg:mt-3">
            <Typography
              className="mr-1 text-18 !font-normal text-[#333333] lg:text-18"
              fontSize={18}
              lineHeight="loose"
              fontWeight="medium"
            >
              RRP
            </Typography>
            <div className="flex flex-row gap-1">
              <Typography
                className="mr-1 text-18 text-black lg:text-18"
                fontSize={18}
                lineHeight="loose"
                fontWeight="medium"
              >
                {formatCurrency(product?.price, product?.currency)}
              </Typography>

              {/* <div className="mr-2 flex items-end text-xs  leading-6 text-zinc-800 lg:pb-1 xl:text-sm   ">
                (inc. GST)
              </div> */}
              {locale === 'au' && product?.isGSTExempt ? (
                <div className="mr-2 flex items-end text-xs leading-6 text-zinc-800 lg:pb-1 xl:text-sm">
                  (GST Free)
                </div>
              ) : (
                <div className="mr-2 flex items-end text-xs leading-6 text-zinc-800 lg:pb-1 xl:text-sm">
                  (inc. GST)
                </div>
              )}
            </div>
          </div> : (
            <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              window.open(`/contact-us/`);
            }}
            className="flex items-center gap-3 px-4 py-3 bg-[#D9EFF1] rounded-md cursor-pointer text-black shadow-sm print:hidden w-fit"
          >
            <FaInfoCircle className="text-[20px] md:text-[22px] 3xl:text-[24px]  flex-shrink-0" />
                <p className="m-0 text-sm leading-5 text-left">
                  {isBuilderRange ? (
                    <>
                      This is part of GWA's Builder range. For pricing, Please contact your GWA account manager
                      {/* <Link href="/contact-us/" className="underline">
                        contact us
                      </Link> */}
                      .
                    </>
                  ) : product?.lifecycle?.toUpperCase()?.trim().includes('PROTOTYPE') ? (
                    <>
                      For more information regarding this product, please{" "}
                      <Link href="/contact-us/" className="underline">
                        contact us
                      </Link>.
                    </>
                  ) : (
                    <>
                      For more information regarding this product, please{" "}
                      <Link href="/contact-us/" className="underline">
                        contact us
                      </Link>
                      .
                    </>
                  )}
                </p>
          </button>
        )}
          <div className="flex sm:gap-1 cursor-pointer" onClick={handleAddToWishlist}>
            <Typography
              className="mr-1 text-18 !font-normal text-[#333333] lg:text-18 pt-3"
              fontSize={18}
              lineHeight="loose"
              fontWeight="medium"
            >
              Add to Saved Products
            </Typography>
            <button
              className="mt-3 flex items-center gap-2 transition-colors cursor-pointer"
              // onClick={handleAddToWishlist}
              aria-label="Add to wishlist"
            >
              {isWishlisted
                ? <StarIconSolid className="h-6 w-6 text-gray-800 cursor-pointer" />
                : <StarIcon className="h-6 w-6 text-gray-600 cursor-pointer" />
              }
            </button>
          </div>
          {product?.lifecycle?.toUpperCase()?.trim() === 'PROTOTYPE' && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => handleAddToProject?.(product)}
                className="w-full sm:w-auto px-8 py-2.5 bg-[#A6C9CC] text-black font-semibold rounded-md shadow-sm cursor-pointer hover:bg-[#8eb8bb] transition-colors"
              >
                Add to Project
              </button>
            </div>
          )}
          {variationUI ? <div className="mb-6 mt-10">{variationUI}</div> : <></>}
        </div>
        <div className="hidden lg:block lg:ml-auto">
          <DeliveryDetails
            productInventory={productInventory}
            customerQuantityPrice={customerQuantityPrice}
            product={product}
            deliveryEstimate={deliveryEstimate}
            loading={isLoading}
            isPriceLoading={isPriceLoading}
            productType={productType}
            isBuilderRange={isBuilderRange}
            setQuantity={setQuantity}
            quantityPrice={quantityPrice}
            quantity={quantity}
            variantKey={variantKey}
            itemCode={itemCode}
            quantityPriceLoading={quantityPriceLoading}
            setPostalCode={setPostalCode}
            postalCode={postalCode}
            loadingDeliverEstimate={loadingDeliverEstimate}
            warehouse={warehouse}
            hasOutOfStockProductPart={hasOutOfStockProductPart}
          />
        </div>
      </div>
    </div>
  );
};

export default Header;